/**
 * Closed accounts (is_active false) are hidden on the Banks page unless asked for, and so is a bank
 * whose every account is closed. These read raw account data and the models alike, since both carry
 * `is_active` and `accounts`.
 *
 * Closed accounts never count in the page's totals, shown or not; see `countsInTotals` in
 * accountSections.js.
 */

export function isClosedAccount(account) {
	return account?.is_active === false;
}

/** The institution's accounts that are rendered. */
export function visibleAccounts(institution, showClosed) {
	const accounts = institution?.accounts || [];
	if(showClosed) {
		return accounts;
	}
	return accounts.filter((account) => !isClosedAccount(account));
}

/** Whether the institution gets a card: only when it has an account to put on it. */
export function isInstitutionVisible(institution, showClosed) {
	return visibleAccounts(institution, showClosed).length > 0;
}

export function countClosedAccounts(institutions) {
	let count = 0;
	for(let institution of institutions || []) {
		for(let account of institution.accounts || []) {
			if(isClosedAccount(account)) {
				count++;
			}
		}
	}
	return count;
}

/**
 * A drag reports positions in the list on screen, and the reorder works on the whole list, hidden
 * items included. This turns the one into the other: the dragged item lands just before the item
 * it now precedes on screen, or just after the one it follows when it went to the end, and every
 * hidden item keeps its place relative to its neighbours.
 *
 * The result has the reorder's own meaning: remove at `startIndex`, then insert at `endIndex` in
 * what is left.
 *
 * @param fullList {Array}
 * @param isVisible {function(*): boolean}
 * @param startIndex {number} on screen
 * @param endIndex {number} on screen
 * @returns {{startIndex: number, endIndex: number}}
 */
export function visibleMoveToFullMove(fullList, isVisible, startIndex, endIndex) {
	const visible = fullList.filter(isVisible);
	const moved = visible[startIndex];
	const fullStart = fullList.indexOf(moved);

	const visibleAfter = visible.filter((item) => item !== moved);
	visibleAfter.splice(endIndex, 0, moved);
	const fullWithout = fullList.filter((item) => item !== moved);

	const next = visibleAfter[endIndex + 1];
	if(next !== undefined) {
		return { startIndex: fullStart, endIndex: fullWithout.indexOf(next) };
	}
	const previous = visibleAfter[endIndex - 1];
	if(previous !== undefined) {
		return { startIndex: fullStart, endIndex: fullWithout.indexOf(previous) + 1 };
	}
	return { startIndex: fullStart, endIndex: fullStart };
}
